import { Box, SxProps, Typography, useTheme } from "@mui/material";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  DETAIL_TAB2POINT_CLOUD_TYPE,
  EUpdateStatus,
  EViewerMode,
  ProjectDetailContext,
} from "./Interfaces/ProjectDetailInterfaces";
import CalcContext from "./Contexts/CalcContext";
import HelmContext from "./Contexts/HelmContext";
import { MeasurementType } from "../../viewer/measurement/types";
import { ECoordinateTransformMode } from "../config/componentsConfig";
import { UpdateInfoContext } from "./Contexts/UpdateInfoContext";
import { projectPointCloudAPIs } from "../../api/project";
import RemoveContext from "./Contexts/RemoveContext";

type Props = {
  height: string;
  sx?: SxProps;
};

export const CurrentModeTXT = (props: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { currentViewerMode, projectDetail, detailTab } =
    useContext(ProjectDetailContext);
  const { measurementType } = useContext(CalcContext);
  const { currentHelmTab } = useContext(HelmContext);
  const { updateAt, setUpdateAt } = useContext(UpdateInfoContext);

  useEffect(() => {
    const fetchUpdateInfo = async () => {
      try {
        const res = await projectPointCloudAPIs.measurement.get(
          projectDetail.id,
          DETAIL_TAB2POINT_CLOUD_TYPE(detailTab)
        );
        setUpdateAt(res.data[0]?.updateAt);
      } catch (error) {
        console.error("Error fetching update info:", error);
      }
    };

    fetchUpdateInfo();
  }, [detailTab, currentViewerMode]);
  const { totalCount, maxCountSelection } = useContext(RemoveContext);

  const mainMode = () => {
    switch (currentViewerMode) {
      case EViewerMode.CALCULATION:
        return t("MEASUREMENT");
      case EViewerMode.HELMERT_TRANSFORM:
        return t("COORDINATE_TRANSFOR");
      case EViewerMode.POINT_REMOVE:
        return t("REMOVE_POINT_GROUP");
      case EViewerMode.EXPORT_TO_OTHER:
        return t("UPLOAD");
    }
  };

  const subMode = () => {
    if (currentViewerMode === EViewerMode.CALCULATION) {
      switch (measurementType) {
        case MeasurementType.VOLUME_CALCULATION:
          return `(${t("VOLUME")})`;
        case MeasurementType.TWO_POINT:
          return `(${t("DISTANCE_TWO_POINTS")})`;
        case MeasurementType.MULTIPLE_POINT:
          return `(${t("DISTANCE_MULTIPLE_POINTS")})`;
        case MeasurementType.HORIZONTAL:
          return `(${t("HORIZONTAL_DISTANCE")})`;
        case MeasurementType.VERTICAL:
          return `(${t("VERTICAL_DISTANCE")})`;
        case MeasurementType.FLAT_AREA_CALCULATION:
          return `(${t("FLAT_AREA")})`;
        case MeasurementType.SURFACE:
          return `(${t("SURFACE_DISTANCE")})`;
      }
    } else if (currentViewerMode === EViewerMode.HELMERT_TRANSFORM) {
      switch (currentHelmTab) {
        case ECoordinateTransformMode.INPUT:
          return `(${t("COORD_INPUT")})`;
        case ECoordinateTransformMode.TRANSFORM:
          return `(${t("COORDINATE_TRANSFOR")})`;
        case ECoordinateTransformMode.VERIFICATION:
          return `(${t("VALID_POINT_ACCURACY_CHECK")})`;
      }
    } else if (currentViewerMode === EViewerMode.POINT_REMOVE) {
      return ` (${totalCount}/${maxCountSelection})`;
    }
    return;
  };

  const updateInfoMode = () => {
    if (currentViewerMode !== EViewerMode.CALCULATION) return "";

    if (!updateAt) return "";

    if (updateAt === EUpdateStatus.SAVED) {
      return ` - (${t("LAST_UPDATE")} ☁ ${t("SAVED")})`;
    }

    if (updateAt === EUpdateStatus.SAVE_FAILED_FORBIDDEN) {
      return ` - (${t("LAST_UPDATE")} ☁ ${t("SAVE_FAILED_FORBIDDEN")})`;
    }

    if (updateAt === EUpdateStatus.SAVE_FAILED_ERROR) {
      return ` - (${t("LAST_UPDATE")} ☁ ${t("SAVE_FAILED_ERROR")})`;
    }

    const formattedTime = new Date(updateAt).toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

    return ` - (${t("LAST_UPDATE")} ${formattedTime})`;
  };

  return !mainMode() ? (
    <></>
  ) : (
    <Typography
      sx={{
        ...props.sx,
        padding: "0px 4px",
        margin: "0px",
        height: props.height,
        lineHeight: `calc(${props.height} - 4px)`,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        color: theme.palette.base.contrast,
        backgroundColor: theme.palette.base.main,
        border: `1px solid ${theme.palette.base.border}`,
        userSelect: "none",
      }}
      variant="subtitle2"
      component="div"
    >
      {mainMode()}
      {subMode()}
      {updateInfoMode()}
    </Typography>
  );
};
